/**
 * @fileoverview Kick Flow - A modern TypeScript library for interacting with the Kick.com API
 * @version 1.0.0
 * @author Kick Flow Team
 * @license MIT
 */

import { KickFlow } from "./client";
import type { KickFlowConfig } from "./types";

// Export the main client class
export { KickFlow } from "./client";

// Export all type definitions
export * from "./types";

// Export error classes for proper error handling
export * from "./common/errors";

// Export utility classes for advanced usage
export { PKCEUtils } from "./common/pkce";
export { OAuthManager } from "./common/oauth-manager";
export { HttpClient } from "./common/http-client";
export { DebugLogger } from "./common/logger";

// Export WebSocket client for real-time chat functionality
export { KickWebSocket } from "./common/websocket";

// Export API utility functions
export { getChannelData, getChatroomId } from "./common/api";

// Export module classes for advanced usage
export { CategoriesModule } from "./modules/categories";
export { ChannelsModule } from "./modules/channels";
export { LivestreamsModule } from "./modules/livestreams";
export { ChatModule } from "./modules/chat";

/**
 * The main client interface without internal methods exposed
 * This provides a clean API surface for end users
 */
export type Client = Omit<KickFlow, "request">;

/**
 * Factory function to create a new Kick API client instance
 * 
 * @example
 * ```typescript
 * // Bot authentication (server-to-server)
 * const kickClient = new client({
 *   clientId: "your-client-id",
 *   clientSecret: "your-client-secret",
 * });
 * 
 * // User authentication (OAuth 2.1)
 * const kickClient = new client({
 *   clientId: "your-client-id",
 *   clientSecret: "your-client-secret",
 *   redirectUri: "http://localhost:3000/callback",
 * });
 * ```
 * 
 * @param config - Configuration object for the Kick API client
 * @returns A new Kick API client instance
 */
export const client = KickFlow as new (config: KickFlowConfig) => Client;

/**
 * Default export for convenience
 * Allows `import KickFlow from '@gbrlstr/kick-flow'`
 */
export default {
  client,
  KickFlow,
};

/**
 * Version information
 */
export const VERSION = "1.0.0";

/**
 * Library metadata
 */
export const LIBRARY_INFO = {
  name: "@gbrlstr/kick-flow",
  version: VERSION,
  description: "A modern, zero-dependency TypeScript library for interacting with the Kick.com API",
  author: "Kick Flow Team",
  license: "MIT",
  repository: "https://github.com/gbrlstr/kick-flow",
  documentation: "https://gbrlstr.github.io/kick-flow",
} as const;